export interface ViewportRect {
  width: number;
  height: number;
}

export interface GamePoint {
  x: number;
  y: number;
}

/** Whether the viewport is portrait (taller than wide). Drives canvas-dim
 *  swap in main.ts and layout selection in TableScene/UIScene. */
export function shouldRotate(viewport: ViewportRect): boolean {
  return viewport.height > viewport.width;
}

/**
 * Identity passthrough today (the CSS-rotation hack is gone now that
 * portrait has a native layout). Kept for orientation.test.ts compatibility.
 */
export function mapClientToGame(
  clientX: number,
  clientY: number,
  _rotated: boolean,
  _viewport: ViewportRect,
): GamePoint {
  return { x: clientX, y: clientY };
}

type ScaleHook = {
  transformX: (pageX: number) => number;
  transformY: (pageY: number) => number;
  refresh: () => void;
};
type Phaser3Game = {
  scale: ScaleHook;
  canvas: HTMLCanvasElement;
};

export interface OrientationDeps {
  game: Phaser3Game;
  parent: HTMLElement;
  getViewport: () => ViewportRect;
}

/**
 * Pins the canvas's CSS dims to its internal-buffer dims (divided by DPR)
 * so rendering stays 1:1 with the buffer. No rotation — portrait gets its
 * own native layout via portraitLayout().
 *
 * Phaser's pointer-coord transform is overridden to convert from
 * page-space to canvas-internal-buffer-space using the live bounding rect.
 * This handles DPR scaling correctly across iOS address-bar collapse and
 * any letterboxing.
 */
export function initOrientation(deps: OrientationDeps): void {
  const { game, parent } = deps;
  const canvas = game.canvas;

  game.scale.transformX = (pageX: number): number => {
    const rect = canvas.getBoundingClientRect();
    return (pageX - rect.left) * (canvas.width / rect.width);
  };
  game.scale.transformY = (pageY: number): number => {
    const rect = canvas.getBoundingClientRect();
    return (pageY - rect.top) * (canvas.height / rect.height);
  };

  parent.style.position = 'relative';
  canvas.style.position = 'absolute';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.transformOrigin = 'top left';

  const apply = () => {
    const dpr = window.devicePixelRatio || 1;
    const naturalW = canvas.width / dpr;
    const naturalH = canvas.height / dpr;
    canvas.style.width  = `${naturalW}px`;
    canvas.style.height = `${naturalH}px`;
    canvas.style.transform = '';
  };

  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
}
